import ShareAcceptModal from "@/components/Sharing/ShareAcceptModal"
import ShareCancelModal from "@/components/Sharing/ShareCancelModal"
import ShareCancelShareModal from "@/components/Sharing/ShareCancelShareModal"
import ShareDeclineModal from "@/components/Sharing/ShareDeclineModal"
import ShareDuplicateModal from "@/components/Sharing/ShareDuplicateModal"
import ShareEndModal from "@/components/Sharing/ShareEndModal"
import ShareEndShareModal from "@/components/Sharing/ShareEndShareModal"
import ShareResponseModal from "@/components/Sharing/ShareResponseModal"
import type { SharedDataJsonldReadCollectionSharedData } from "@/networking/api"
import { isNotBlank } from "@/utils/ts-utils"
import { useDisclosure } from "@chakra-ui/react"
import { useMemo, useState } from "react"

const getItemById = (items: SharedDataJsonldReadCollectionSharedData[], id: string) => {
  for (const item of items) {
    if (item.id === id) return item
  }
  return {} as SharedDataJsonldReadCollectionSharedData
}

// The InterBU detail modal and every modal it opens (accept, decline, end…), shared by the
// table and the cards (ADR 0009, D3). openDetails(id) shows the detail of a listed share; the
// other openers go straight to an action's confirmation modal.
export const useSharedDataModals = (
  items: SharedDataJsonldReadCollectionSharedData[] | undefined,
  fromOrTo: "from" | "to",
  refreshQueries?: () => void
) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { isOpen: isOpenAccept, onOpen: onOpenAccept, onClose: onCloseAccept } = useDisclosure()
  const { isOpen: isOpenDecline, onOpen: onOpenDecline, onClose: onCloseDecline } = useDisclosure()
  const { isOpen: isOpenCancel, onOpen: onOpenCancel, onClose: onCloseCancel } = useDisclosure()
  const {
    isOpen: isOpenCancelShare,
    onOpen: onOpenCancelShare,
    onClose: onCloseCancelShare,
  } = useDisclosure()
  const { isOpen: isOpenEnd, onOpen: onOpenEnd, onClose: onCloseEnd } = useDisclosure()
  const {
    isOpen: isOpenEndShare,
    onOpen: onOpenEndShare,
    onClose: onCloseEndShare,
  } = useDisclosure()
  const {
    isOpen: isOpenDuplicate,
    onOpen: onOpenDuplicate,
    onClose: onCloseDuplicate,
  } = useDisclosure()

  const [currentItemId, setCurrentItemId] = useState<string>("")
  const currentItem = useMemo(() => getItemById(items ?? [], currentItemId), [items, currentItemId])

  const [isKo, setIsKo] = useState(false)
  const onOpenEndOk = () => {
    setIsKo(false)
    onOpenEnd()
  }
  const onOpenEndKo = () => {
    setIsKo(true)
    onOpenEndShare()
    // onOpenEnd()
  }

  const [isRevoke, setIsRevoke] = useState(false)
  const onOpenRevoke = () => {
    setIsRevoke(true)
    onOpenDecline()
  }

  const openDetails = (id: string) => {
    setCurrentItemId(id)
    if (isNotBlank(id)) onOpen()
  }

  // Card shortcuts (ADR 0009, D3, revised on 2026-10-05): straight to the confirmation modal of
  // the action, the same one the detail modal opens.
  const openWith = (open: () => void) => (id: string) => {
    if (!isNotBlank(id)) return
    setCurrentItemId(id)
    open()
  }
  const openAccept = openWith(onOpenAccept)
  const openDecline = openWith(() => {
    setIsRevoke(false)
    onOpenDecline()
  })
  const openCancelShare = openWith(onOpenCancelShare)

  const modals = (
    <>
      <ShareResponseModal
        isOpen={isOpen}
        onClose={onClose}
        onOpenAccept={onOpenAccept}
        onOpenDecline={onOpenDecline}
        onOpenRevoke={onOpenRevoke}
        onOpenCancel={onOpenCancel}
        onOpenCancelShare={onOpenCancelShare}
        onOpenEndOk={onOpenEndOk}
        onOpenEndKo={onOpenEndKo}
        onDuplicate={onOpenDuplicate}
        refreshQueries={refreshQueries}
        sharedEntity={currentItem}
        fromOrTo={fromOrTo}
      />
      <ShareAcceptModal isOpen={isOpenAccept} onClose={onCloseAccept} sharedEntity={currentItem} />
      <ShareDeclineModal
        isOpen={isOpenDecline}
        onClose={onCloseDecline}
        sharedEntity={currentItem}
        isRevoke={isRevoke}
      />
      <ShareCancelModal isOpen={isOpenCancel} onClose={onCloseCancel} sharedEntity={currentItem} />
      <ShareCancelShareModal
        isOpen={isOpenCancelShare}
        onClose={onCloseCancelShare}
        sharedEntity={currentItem}
      />
      <ShareEndShareModal
        isOpen={isOpenEndShare}
        onClose={onCloseEndShare}
        sharedEntity={currentItem}
      />
      <ShareEndModal
        isOpen={isOpenEnd}
        onClose={onCloseEnd}
        sharedEntity={currentItem}
        isKo={isKo}
      />
      <ShareDuplicateModal
        isOpen={isOpenDuplicate}
        onClose={refresh => {
          onCloseDuplicate()
          if (refresh) refreshQueries?.()
        }}
        sharedEntity={currentItem}
      />
    </>
  )

  return { openDetails, openAccept, openDecline, openCancelShare, modals }
}
